import { localize } from '../i18n';
import useNavigate from '../hooks/useSafeNavigate';
import SingleLineText from './ui/SingleLineText';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { colors } from '../design/tokens';

// Android는 RN SafeAreaView가 이미 status bar 영역을 잘라낸 뒤 WebView를 마운트 → 8px 고정.
// iOS는 노치/Dynamic Island 회피용으로 safe-area-inset-top만큼만 push (추가 여백 없음).
const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
const TOP_PADDING = isAndroid ? '8px' : 'env(safe-area-inset-top, 0px)';

export default function PageHeader({ title, subtitle, back, rightAction, onTap, maxWidth = 480, matchContentHeight = false }) {
    const navigate = useNavigate();
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(null);
    useLayoutEffect(() => {
        if (!matchContentHeight || !headerRef.current) return;
        const measure = () => setHeaderHeight(headerRef.current.getBoundingClientRect().height);
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(headerRef.current);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, [matchContentHeight]);
    const handleBack = (event) => {
        event?.stopPropagation();
        if (typeof back === 'function') back();
        else navigate(-1);
    };

    return (
        <>
            {/* Fixed header */}
            <Box
                ref={headerRef}
                data-page-header
                onClick={onTap}
                {...(onTap ? { role: 'button', tabIndex: 0, 'data-tappable': 'true' } : {})}
                sx={{
                    position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
                    width: '100%', maxWidth, zIndex: 100,
                    pt: TOP_PADDING, pb: 1, px: 2, bgcolor: colors.surface,
                    borderBottom: `1px solid ${colors.borderNeutral}`,
                    overflow: 'visible',
                    cursor: onTap ? 'pointer' : 'default',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                        {back && (
                            <IconButton
                                aria-label={localize("뒤로가기")}
                                onClick={handleBack}
                                sx={{ color: colors.text, width: 48, height: 48, p: 0, ml: -1.5, flexShrink: 0 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <SingleLineText sx={{ fontSize: '1.2rem', fontWeight: 800, color: colors.text }}>
                                {title}
                            </SingleLineText>
                            {subtitle && (
                                <SingleLineText sx={{ fontSize: '0.95rem', color: '#777', mt: 0.1 }}>
                                    {subtitle}
                                </SingleLineText>
                            )}
                        </Box>
                    </Box>
                    {rightAction && rightAction}
                </Box>
            </Box>
            {/* 홈·설정은 실제 헤더 높이를 사용해 글꼴/안전 영역 변화에도 같은 간격을 유지한다. */}
            <Box sx={matchContentHeight && headerHeight !== null ? { height: headerHeight } : { pt: isAndroid
                ? (subtitle ? '64px' : (rightAction ? '58px' : '48px'))
                : (subtitle
                    ? 'calc(env(safe-area-inset-top, 0px) + 56px)'
                    : (rightAction
                        ? 'calc(env(safe-area-inset-top, 0px) + 50px)'
                        : 'calc(env(safe-area-inset-top, 0px) + 40px)'))
            }} />
        </>
    );
}
